import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import MoveItemsModal from '../../../../components/move-items-modal'
import { useSnackbar } from '../../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../../components/stories/molecules/snackbar/constants'
import {
  planMenuApiErrorCodeOptions,
  planMenuItemTypeOptions
} from '../../constants'
import { useCheckPlanChildrenNameIsAvailableMutation } from '../../../../react-query/plan/mutation/use-check-plan-children-name-is-available-mutation'
import { isEmpty } from 'lodash'
import NotAllowedMoveItemsAlert from '../../../../components/move-items-modal/not-allowed-move-items-alert'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { recursiveListTypeOptions } from '../../../../components/recursive-folder-list/constants'
import { useMovePlansAndFoldersMutation } from '../../../../react-query/plan/mutation/use-move-plans-and-folders-mutation'
import { getRootPlansWithFolders } from '../../../PlanSelection/actions'
import { useDispatch } from 'react-redux'
import { useGetProjectDetailsQuery } from '../../../../react-query/project/query/use-get-project-details-query'

const MoveMultipleItemsModal = ({
  onClose = () => {},
  onMoved = () => {},
  selectedFolders = [],
  selectedPlans = []
}) => {
  const { formatMessage } = useIntl()
  const { addSnackbar } = useSnackbar()
  const dispatch = useDispatch()

  const [moveItemsError, setMoveItemsError] = useState('')

  const { projectId } = useParams()

  const { data: projectDetails, isLoading: projectDetailsLoading } =
    useGetProjectDetailsQuery({
      projectId
    })

  const planRootFolderId = projectDetails?.planRootFolderId

  // items currently targeted by the move action - starts as the items
  // received via props, but gets replaced with only the blocked items
  // when the user chooses to move the remaining items after a partial move
  const [selectedItems, setSelectedItems] = useState({
    folders: selectedFolders,
    plans: selectedPlans
  })

  // items blocked by the last move attempt (name conflict in destination)
  const [blockedItems, setBlockedItems] = useState({
    folders: [],
    plans: []
  })

  const selected = useMemo(
    () => ({
      folders: {
        ids: selectedItems.folders.map((f) => f.id),
        names: selectedItems.folders.map((f) => f.name)
      },
      plans: {
        ids: selectedItems.plans.map((p) => p.id),
        names: selectedItems.plans.map((p) => p.name)
      }
    }),
    [selectedItems]
  )

  const totalSelectedItems =
    selected.folders.ids.length + selected.plans.ids.length

  const hasBlockedItems =
    !isEmpty(blockedItems.folders) || !isEmpty(blockedItems.plans)

  const blockedItemsList = useMemo(
    () => [
      ...blockedItems.folders.map((folder) => ({
        name: folder.name,
        itemType: planMenuItemTypeOptions.FOLDER
      })),
      ...blockedItems.plans.map((plan) => ({
        name: plan.name,
        itemType: planMenuItemTypeOptions.PLAN
      }))
    ],
    [blockedItems]
  )

  const [destinationFolderHint, setDestinationFolderHint] = useState(null)

  // handlers
  const getPlanTree = () => dispatch(getRootPlansWithFolders(projectId))

  const showSnackbarSuccess = () => {
    addSnackbar({
      message: formatMessage({ id: 'moveItems_items_moved' }),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  // replaces the selected items in state with only the items that were
  // blocked on the last move attempt, and closes the blocked items alert
  const handleMoveRemainingItems = () => {
    setSelectedItems({
      folders: blockedItems.folders,
      plans: blockedItems.plans
    })
    setBlockedItems({ folders: [], plans: [] })
  }

  // handle callbacks
  const handleMoveItemsSuccess = () => {
    getPlanTree()

    // there are still blocked items pending review, keep the modal open
    // so the user can decide what to do with them
    if (hasBlockedItems) return null

    if (isEmpty(moveItemsError)) {
      onClose()
      onMoved()
      showSnackbarSuccess()
    }
  }

  const handleMoveItemsError = (error) => {
    // user doesn't have permission to move items
    if (
      error &&
      error?.response?.data?.status ===
        planMenuApiErrorCodeOptions.NO_PERMISSION_TO_CREATE_PLAN_OR_FOLDER
    ) {
      return setMoveItemsError(
        formatMessage({
          id: 'projectMenu_no_permission_to_move'
        })
      )
    }

    // folder with the same name already exists in the destination folder
    if (
      error &&
      error?.response?.data?.status ===
        planMenuApiErrorCodeOptions.FOLDER_NAME_IS_NOT_AVAILABLE
    ) {
      return setDestinationFolderHint(
        formatMessage({
          id: 'projectMenu_folder_with_this_name_aready_exists'
        })
      )
    }

    if (
      error &&
      error?.response?.data?.status ===
        planMenuApiErrorCodeOptions.FOLDER_NO_LOGER_EXISTS
    ) {
      return setDestinationFolderHint(
        formatMessage({
          id: 'recursiveFolderList_the_selected_folder_no_longer_exists'
        })
      )
    }

    return setMoveItemsError(
      formatMessage({
        id: 'moveItems_move_error'
      })
    )
  }

  const handleCheckMoveItemsNameSuccess = (data, variables) => {
    const { planNames: checkedItemNames } = data
    const { destinationFolderId } = variables

    // items already inside the destination folder must not be moved
    // root level items have a nullish parentId, so fall back to the root folder id
    const namesAlreadyInDestination = [
      ...selectedItems.folders,
      ...selectedItems.plans
    ]
      .filter(
        (item) => (item.parentId ?? planRootFolderId) === destinationFolderId
      )
      .map((item) => item.name)

    // split the object into 2 arrays - allowed and blocked
    const allowedNames = Object.entries(checkedItemNames)
      .filter(
        ([name, item]) =>
          item.allowed && !namesAlreadyInDestination.includes(name)
      )
      .map(([name]) => name)

    // move only allowed items
    const allowedFolderIds = selectedItems.folders
      .filter((folder) => allowedNames.includes(folder.name))
      .map((folder) => folder.id)

    const allowedPlanIds = selectedItems.plans
      .filter((plan) => allowedNames.includes(plan.name))
      .map((plan) => plan.id)

    // keep track of the blocked items so they can replace the current
    // selection if the user chooses to move the remaining items
    const newBlockedFolders = selectedItems.folders.filter(
      (folder) => !allowedNames.includes(folder.name)
    )
    const newBlockedPlans = selectedItems.plans.filter(
      (plan) => !allowedNames.includes(plan.name)
    )

    if (!isEmpty(newBlockedFolders) || !isEmpty(newBlockedPlans)) {
      setBlockedItems({
        folders: newBlockedFolders,
        plans: newBlockedPlans
      })
    }

    if (isEmpty(allowedPlanIds) && isEmpty(allowedFolderIds)) return null

    moveItems({
      planIds: allowedPlanIds,
      folderIds: allowedFolderIds,
      destinationFolderId,
      projectId
    })
  }

  const handleCheckMoveItemsNameError = () => {
    return setMoveItemsError(
      formatMessage({
        id: 'moveItems_move_error'
      })
    )
  }

  // mutations

  const { mutate: moveItems, isPending: moveItemsLoading } =
    useMovePlansAndFoldersMutation({
      onSuccess: handleMoveItemsSuccess,
      onError: handleMoveItemsError
    })

  const { mutate: checkMoveItems, isPending: checkMoveItemsLoading } =
    useCheckPlanChildrenNameIsAvailableMutation({
      onSuccess: handleCheckMoveItemsNameSuccess,
      onError: handleCheckMoveItemsNameError
    })

  const handleMoveItems = ({ parentFolderId = null }) => {
    setMoveItemsError(null)
    setDestinationFolderHint(null)

    const selectedItemNames = [
      ...selected.folders.names,
      ...selected.plans.names
    ]

    const filterDuplicatedItems = [...new Set(selectedItemNames)]

    checkMoveItems({
      selectedItemNames: filterDuplicatedItems,
      destinationFolderId: parentFolderId ?? planRootFolderId
    })
  }

  const isLoading = moveItemsLoading || checkMoveItemsLoading

  if (totalSelectedItems === 0) return null

  return (
    <>
      <NotAllowedMoveItemsAlert
        isOpen={hasBlockedItems}
        blockedItems={blockedItemsList}
        totalItems={totalSelectedItems}
        onClose={onClose}
        onMoveRemaining={handleMoveRemainingItems}
      />
      {!hasBlockedItems && (
        <MoveItemsModal
          handleClose={onClose}
          handleMove={handleMoveItems}
          isLoading={projectDetailsLoading}
          onMoveLoading={isLoading}
          onMoveError={moveItemsError}
          destinationFolderHint={destinationFolderHint}
          modalTitle={formatMessage({ id: 'move' })}
          isMultipleItems={true}
          totalSelectedItems={totalSelectedItems}
          planRootFolderId={planRootFolderId}
          foldersListType={recursiveListTypeOptions.PLAN}
          excludedFolderIds={selected.folders.ids}
        />
      )}
    </>
  )
}

MoveMultipleItemsModal.propTypes = {
  onClose: PropTypes.func,
  onMoved: PropTypes.func,
  selectedFolders: PropTypes.array,
  selectedPlans: PropTypes.array
}

export default MoveMultipleItemsModal
